/**
 * 帳戶餘額計算 — 單一權威實作(FIN-11)
 *
 * 語意約定(與儲存層 total 欄位一致):
 *   - buy:            total = 成交金額 + 手續費(現金流出,毛額)
 *   - sell:           total = 成交金額 - 手續費 - 交易稅(現金流入,淨額)
 *   - dividend:       total = 股利淨額(已扣費用/稅金)
 *   - cash_*:         total = 存提款金額
 *   - adjustmentAmount: 額外的現金調整(正負皆可)
 *
 * 計算規則:
 *   新餘額 = initialBalance + Σ transactionEffect(t)
 *   全量重算與單筆增量一律使用本模組,不允許平行實作。
 */

import { Transaction } from '../types';

/** 單筆交易對現金餘額的影響(以儲存的 total 為準) */
export function calculateTransactionBalanceEffect(transaction: Transaction): number {
  const { type, total, adjustmentAmount = 0 } = transaction;

  switch (type) {
    case 'buy':
      // 買入:現金流出(total 已含手續費)
      return -(total + adjustmentAmount);
    case 'sell':
    case 'dividend':
      // 賣出/股利:現金流入(total 已是淨額)
      return total + adjustmentAmount;
    case 'cash_deposit':
      return Math.abs(total) + adjustmentAmount;
    case 'cash_withdrawal':
      return -Math.abs(total) + adjustmentAmount;
    case 'stock_increase':
    case 'stock_decrease':
      // 股票異動不影響現金,僅調整金額影響
      return adjustmentAmount;
    default:
      return 0;
  }
}

/**
 * 全量重算:initialBalance + 所有交易效果
 * @param initialBalance 帳戶初始餘額(優先 initialBalance,向下相容 fallback balance)
 * @param transactions 該帳戶全部交易
 */
export function calculateBalanceFromTransactions(
  initialBalance: number,
  transactions: Transaction[]
): number {
  let effects = 0;
  for (const transaction of transactions) {
    effects += calculateTransactionBalanceEffect(transaction);
  }
  return Math.round(initialBalance + effects);
}

/**
 * 解析帳戶文件,取得重算用的初始餘額(向下相容:無 initialBalance 的舊帳戶)
 */
export function resolveInitialBalance(accountData: {
  initialBalance?: number | null;
  balance?: number | null;
}): number {
  return accountData.initialBalance ?? accountData.balance ?? 0;
}
